// Revision-cloud geometry (TL-6 Marker cloud). Stored as 2 bbox corner points; the
// scalloped boundary is generated at render/export time so it stays editable as a rect.
import type { Vec } from "./geometry";

const SEG_PER_SCALLOP = 8;

/**
 * Closed densified polyline tracing a scalloped rectangle between corners `a` and `b`.
 * Each scallop is a semicircle of ~`rMm` bulging outward. Returns [] for a degenerate box.
 */
export function cloudScallops(a: Vec, b: Vec, rMm: number): Vec[] {
  const minX = Math.min(a.x, b.x);
  const minY = Math.min(a.y, b.y);
  const maxX = Math.max(a.x, b.x);
  const maxY = Math.max(a.y, b.y);
  const w = maxX - minX;
  const h = maxY - minY;
  if (w < rMm || h < rMm) return [];

  const corners: Vec[] = [
    { x: minX, y: minY },
    { x: maxX, y: minY },
    { x: maxX, y: maxY },
    { x: minX, y: maxY }
  ];
  const out: Vec[] = [];
  for (let e = 0; e < 4; e++) {
    const p0 = corners[e]!;
    const p1 = corners[(e + 1) % 4]!;
    const edgeLen = Math.hypot(p1.x - p0.x, p1.y - p0.y);
    const count = Math.max(1, Math.round(edgeLen / (2 * rMm)));
    const ux = (p1.x - p0.x) / edgeLen;
    const uy = (p1.y - p0.y) / edgeLen;
    // outward normal = clockwise 90° of edge dir (rect wound CW under Y-down → points outward)
    const nx = uy;
    const ny = -ux;
    const scallopR = edgeLen / (2 * count);
    for (let s = 0; s < count; s++) {
      const cx = p0.x + ux * scallopR * (2 * s + 1);
      const cy = p0.y + uy * scallopR * (2 * s + 1);
      // semicircle from edge-start side to edge-end side, bulging along +normal
      for (let k = 0; k <= SEG_PER_SCALLOP; k++) {
        const t = (k / SEG_PER_SCALLOP) * Math.PI; // 0..π
        const along = -Math.cos(t); // -1..+1 along the edge
        const outward = Math.sin(t); // 0..1..0 along the normal
        out.push({
          x: cx + ux * scallopR * along + nx * scallopR * outward,
          y: cy + uy * scallopR * along + ny * scallopR * outward
        });
      }
    }
  }
  return out;
}
